import { Component, OnInit, OnDestroy, ViewChild, effect, input, output, untracked } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { ClientInformation } from '../../models/client-information';
import { Integration } from '../../models/integration';
import { IntegrationJavaScript } from '../../models/integration-javascript';
import { MonacoPageComponent } from '../../features/lt-shared-ui/lt-monaco/components/monaco-editor/monaco-editor.component';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { ToastService } from '../../services/toast.service';
import { Observable, Subject } from 'rxjs';
import { MonacoDiffPageComponent } from '../../features/lt-shared-ui/lt-monaco/components/monaco-diff-editor/monaco-diff-editor.component';
import { User } from '../../models/user';
import { DestinationEndpoint } from 'src/app/models/destination-endpoint';
import { getEndpointName } from 'src/app/utilities/data-utils';
@Component({
  selector: 'app-integration-revision',
  templateUrl: './integration-revision.component.html',
  styleUrls: ['./integration-revision.component.scss'],
  providers: [ClientIntegrationService],
  standalone: false,
})
export class IntegrationRevisionComponent implements OnInit, OnDestroy {

  @ViewChild('monaco') public monaco: MonacoPageComponent;
  @ViewChild('monacoHistory') public monacoHistory: MonacoDiffPageComponent;
  @ViewChild('monacoJSHistory') public monacoJSHistory: MonacoDiffPageComponent;

  integration = input<Integration>();
  client = input<ClientInformation>();
  destinationEndpoints = input<DestinationEndpoint[]>([]);
  dataChanged = output<boolean>();

  selectedIntegrationHistory: Integration;
  integrationHistory: Integration[];
  integrationFormControl = new UntypedFormControl('');
  integrationHistoryLoading = true;
  javaScriptHistoryLoading = true;

  filteredDestinationEndpoints: DestinationEndpoint[] = [];
  selectedEndpoint: DestinationEndpoint;
  selectedJavascript: IntegrationJavaScript;
  selectedJavascriptCompare: IntegrationJavaScript;
  javascriptHistoryList: IntegrationJavaScript[];
  endpointFormControl = new UntypedFormControl('');

  historyData = '';
  historyRawData = '';
  rawData = '';

  tmpHistoryData = '';
  tmpHistoryRawData = '';
  tmpRawData = '';

  monacoLangHtml = 'html';
  monacoLangJs = 'javascript';
  historyChecked = true;

  historyChanged = false;
  jsHistoryChanged = false;
  checked = true;

  currentSideTitle = '';
  historySideTitle = '';

  componentDestroyed$ = new Subject<boolean>();

  constructor(
    private clientService: ClientIntegrationService,
    private toastService: ToastService,
    private user: User,
  ) {
    effect(() => {
      const integration = this.integration();
      if (integration) {
        untracked(() => this.loadForIntegration());
      }
    });
  }

  ngOnInit(): void { }

  private loadForIntegration(): void {
    this.integrationHistoryLoading = true;
    this.javaScriptHistoryLoading = true;
    this.clearHistory();
    this.getIntegrationHistory()
      .subscribe((i) => {
        this.integrationHistory = i;
        this.integrationHistory?.sort((a, b) => {
          return (new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime());
        });

        this.integrationHistoryLoading = false;
      });

    this.clientService
      .getIntegrationJavaScriptHistory(
        this.integration().integrationId,
      )
      .subscribe((i) => {
        this.javascriptHistoryList = i;
        this.javascriptHistoryList?.sort((a, b) => {
          return (new Date(b.updatedDate).getTime() - new Date(a.updatedDate).getTime());
        });
        this.javaScriptHistoryLoading = false;
      });

    const newEndpoints = [];
    for (const js of this.integration().javaScript) {
      for (const d of this.destinationEndpoints()) {
        if (d.destinationEndpointId == js.destinationEndpointId) {
          newEndpoints.push(d);
        }
      }
    }
    this.filteredDestinationEndpoints = newEndpoints;
    this.setDefaultEndpoint();
  }

  getIntegrationHistory(): Observable<Integration[]> {
    return this.clientService.getIntegrationHistory(this.integration().integrationId);
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  selectIntegrationHistory(i: Integration) {
    this.selectedIntegrationHistory = i;
    this.historyData = this.selectedIntegrationHistory.xmlConfig;
    this.historyRawData = this.integration().xmlConfig;
    this.currentSideTitle = this.integration().updatedDate.toString();
    this.historySideTitle = this.selectedIntegrationHistory.updatedDate.toString();
  }

  selectEndpointType(d: DestinationEndpoint) {
    this.selectedEndpoint = d;

    for (const js of this.integration().javaScript) {
      if (this.selectedEndpoint.destinationEndpointId == js.destinationEndpointId) {
        this.selectedJavascript = js;
        this.historyRawData = this.selectedJavascript.javaScript;
        this.currentSideTitle = this.getDestName(this.selectedJavascript.destinationEndpointId)
          + ' | ' + this.selectedJavascript.createDate;
      }
    }
  }

  getDestName(destId: number): string {
    if (this.filteredDestinationEndpoints.length === 0 || !destId) {
      return destId.toString();
    }
    return getEndpointName(this.filteredDestinationEndpoints, destId);
  }

  selectJsIntegrationCompare(i: IntegrationJavaScript) {
    this.selectedJavascriptCompare = i;
    this.historyData = this.selectedJavascriptCompare.javaScript;
    this.historySideTitle = this.getDestName(this.selectedJavascriptCompare.destinationEndpointId)
      + ' | ' + this.selectedJavascriptCompare.createDate;
  }

  filterJavascriptHistoryList(d: DestinationEndpoint): IntegrationJavaScript[] {
    const filteredList = [];
    for (const js of this.javascriptHistoryList) {
      if (js.destinationEndpointId == d.destinationEndpointId) {
        filteredList.push(js);
      }
    }
    return filteredList;
  }

  //If there is only 1 JS endpoint, select it
  setDefaultEndpoint() {
    if (this.filteredDestinationEndpoints.length == 1) {
      this.selectedEndpoint = this.filteredDestinationEndpoints[0];
      this.endpointFormControl.setValue(this.selectedEndpoint);
      this.selectEndpointType(this.selectedEndpoint);
    }
  }

  public clearHistory(): void {
    this.historyData = '';
    this.historyRawData = '';
    this.selectedIntegrationHistory = null;
    this.selectedJavascript = null;
    this.selectedJavascriptCompare = null;
    this.selectedEndpoint = null;
    this.setDefaultEndpoint();
  }

  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  updateIntegrationFromHistory(field: string): void {
    let updateMsg = '';
    if (field == 'xml') {
      this.integration().xmlConfig = this.selectedIntegrationHistory.xmlConfig;
      updateMsg = this.selectedIntegrationHistory.updatedDate.toString();

      this.clientService.updateIntegration(this.integration())
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe({
          next: (_value) => {
            const msg = 'Successfully reverted ' + this.integration().name + ' integration XML to ' + updateMsg;

            this.toastService.toastCreate(msg, 'Success', { keepAfterRouteChange: true });
            this.dataChanged.emit(true);
          },
          error: (_error) => {
            this.toastService.toastCreate('Failed to revert to Integration History', 'Warning', {
              autoClose: false,
            });
          },
        });
    }
    else if (field == 'js') {
      for (const js of this.integration().javaScript) {
        if (this.selectedJavascriptCompare.destinationEndpointId == js.destinationEndpointId) {
          js.javaScript = this.selectedJavascriptCompare.javaScript;
          updateMsg = this.getDestName(this.selectedJavascriptCompare.destinationEndpointId)
            + ' ' + this.selectedJavascriptCompare.createDate.toString();

          this.clientService.updateJavaScript(js)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
              next: (_value) => {
                const msg = 'Successfully reverted ' + this.integration().name + ' integration JavaScript to ' + updateMsg;

                this.toastService.toastCreate(msg, 'Success', { keepAfterRouteChange: true });
                this.dataChanged.emit(true);
              },
              error: (_error) => {
                this.toastService.toastCreate('Failed to revert to Integration JavaScript History', 'Warning', {
                  autoClose: false,
                });
              },
            });
        }
      }
    }
  }
}
